import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { UserStatus, UserRoles } from "../types/user.types";

interface ProtectedRoutesProps {
  element: React.ReactElement;
  allowedRoles?: UserRoles[];
  requiredStatus?: UserStatus;
}

const ProtectedRoutes: React.FC<ProtectedRoutesProps> = ({
  element,
  allowedRoles,
  requiredStatus,
}) => {
  const { validateToken, user } = useAuth();
  const location = useLocation();

  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const isValid = await validateToken();
      if (!isValid) {
        setIsAuthenticated(await validateToken());
      } else {
        setIsAuthenticated(true);
      }
      setIsValidating(false);
    };

    checkAuth();
  }, [validateToken]);

  if (isValidating) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} />;
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} />;
  }

  const hasValidRole =
    !allowedRoles || allowedRoles.includes(user.roleId as UserRoles);
  const hasValidStatus = !requiredStatus || user.status === requiredStatus;

  if (!hasValidRole || !hasValidStatus) {
    return <Navigate to="/unauthorized" />;
  }

  return <>{element}</>;
};

export default ProtectedRoutes;
